import React, { useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { submitJoinRequest } from '../lib/api/fellowship';
import Footer from '../components/Footer';
import FellowScrollWall from '../components/FellowScrollWall';
import FellowshipAnimation from '../components/FellowshipAnimation';
import { EASE, VP, FELLOWSHIP_LEVELS, YOM_RATES } from '../lib/fellowshipConstants';
import '../styles/become-a-fellow.css';

const up = (delay = 0) => ({
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.60, ease: EASE, delay } },
});
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

/* ── Scroll feature — Eduaide sticky-scroll pattern ──────────────────────── */
function ScrollFeature({ level, i }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <div className="baf-sf" ref={ref}>

      {/* Feature label — plain text FIRST (like Eduaide: title on top, visual below) */}
      <motion.div
        className="baf-sf-label"
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.10, duration: 0.55, ease: EASE }}
      >
        <span className="baf-sf-feat-tag" style={{ color: level.color }}>{level.tag}</span>
        <h3 className="baf-sf-feat-name">{level.label}</h3>
        <p className="baf-sf-feat-desc">{level.detail}</p>
      </motion.div>

      {/* Separator line — just a line, no card (exactly like Eduaide) */}
      <motion.div
        className="baf-sf-sep"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={inView ? { scaleX: 1, opacity: 1 } : {}}
        transition={{ delay: 0.22, duration: 0.55, ease: EASE }}
      />

      {/* Visual card — elevated UI mockup (like Eduaide's feature screenshot) */}
      <motion.div
        className="baf-sf-card"
        style={{ '--lc': level.color }}
        initial={{ opacity: 0, y: 32 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.18, duration: 0.55, ease: EASE }}
      >
        {/* Card header */}
        <div className="baf-sf-card-top">
          <div className="baf-sf-card-meta">
            <span className="baf-sf-step-badge" style={{ background: level.color + '12', color: level.color }}>
              Step {i + 1} / 4
            </span>
            <span className="baf-sf-tag" style={{ background: level.color + '12', color: level.color }}>
              {level.tag}
            </span>
          </div>
          <h3 className="baf-sf-title">{level.label}</h3>
          <code className="baf-sf-code">{level.code}</code>
        </div>

        {/* Inner divider */}
        <div className="baf-sf-card-div" />

        {/* Criteria rows — Eduaide adapt/differentiate list: colored bar | value | label */}
        <div className="baf-sf-crit">
          {level.criteria.map((c, ci) => (
            <motion.div
              key={ci}
              className="baf-sf-crit-item"
              style={{ borderLeftColor: level.color }}
              initial={{ opacity: 0, x: -14 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.32 + ci * 0.11, duration: 0.38, ease: EASE }}
            >
              <span className="baf-sf-val" style={{ color: level.color }}>{c.value}</span>
              <span className="baf-sf-lbl">{c.label}</span>
            </motion.div>
          ))}
        </div>

        {/* Warning */}
        {level.warning && (
          <motion.div
            className={`baf-sf-warn${level.warningType === 'amber' ? ' baf-sf-warn--amber' : ''}`}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.68, duration: 0.40, ease: EASE }}
          >
            <span className="baf-sf-warn-icon">!</span>
            <p>{level.warning}</p>
          </motion.div>
        )}

        {/* Benefit footer */}
        <div className="baf-sf-foot">
          <CheckCircle size={12} style={{ color: level.color, flexShrink: 0 }} />
          <span>{level.benefit}</span>
          {level.redeem > 0 && (
            <span className="baf-sf-foot-extra">
              &middot; Redeem up to{' '}
              <strong style={{ color: level.color }}>{level.redeem.toLocaleString()} YOMs</strong>
            </span>
          )}
          {level.maxTeam > 0 && (
            <span className="baf-sf-foot-extra">
              &middot; Mentor up to{' '}
              <strong style={{ color: level.color }}>{level.maxTeam}</strong> educators
            </span>
          )}
        </div>
      </motion.div>

    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
export default function BecomeAFellow() {
  const stagesRef      = useRef(null);
  const heroRef        = useRef(null);
  const animationRef   = useRef(null);
  const formRef        = useRef(null);
const stickyOuter    = useRef(null); // holds 300px grid space
  const stickyInner    = useRef(null); // gets position:fixed applied by JS

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const wallY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);

  /* ── JS sticky — bypasses html/body overflow-x:hidden bug ─────────────── */
  useEffect(() => {
    const section = stagesRef.current;
    const outer   = stickyOuter.current;
    const inner   = stickyInner.current;
    if (!section || !outer || !inner) return;

    // align-self:stretch keeps outer at full grid-row height even when inner is
    // position:fixed, so oRect is always accurate for trigger + anchor calculations.
    let anchorLeft  = null;
    let anchorWidth = null;
    let absoluteSet = false; // CRITICAL: set absolute top only once, then don't touch it

    function update() {
      if (window.innerWidth <= 900) {
        inner.style.cssText = '';
        anchorLeft = anchorWidth = null;
        absoluteSet = false;
        return;
      }

      const sRect     = section.getBoundingClientRect();
      const oRect     = outer.getBoundingClientRect();
      const innerH    = inner.offsetHeight;
      // Vertically center the panel so its bottom aligns with the right column's
      // bottom content. Min 40px from top so it never clips under the nav.
      const TOP       = Math.max(40, Math.round((window.innerHeight - innerH) / 2));
      const remaining = oRect.bottom - TOP - innerH;

      if (sRect.top > TOP) {
        // Not yet in section — normal flow
        inner.style.cssText = '';
        anchorLeft  = anchorWidth = null;
        absoluteSet = false;
      } else if (remaining <= 0) {
        if (!absoluteSet) {
          // Snapshot the seamless top ONCE. Setting it on every frame would pin the
          // element at viewport y=40 each frame (same as fixed) — preventing natural scroll.
          const seamlessTop = TOP - oRect.top;
          const w           = anchorWidth || oRect.width;
          inner.style.position = 'absolute';
          inner.style.top      = Math.max(0, seamlessTop) + 'px';
          inner.style.bottom   = '';
          inner.style.left     = '0';
          inner.style.width    = w + 'px';
          inner.style.opacity  = '1';
          anchorLeft  = null;
          anchorWidth = null;
          absoluteSet = true;
        }
        // Do NOT update style again — let it scroll off with the page naturally
      } else {
        absoluteSet = false;
        if (anchorLeft === null) {
          anchorLeft  = oRect.left;
          anchorWidth = oRect.width;
        }
        inner.style.position = 'fixed';
        inner.style.top      = TOP + 'px';
        inner.style.left     = anchorLeft + 'px';
        inner.style.width    = anchorWidth + 'px';
        inner.style.bottom   = '';
        inner.style.opacity  = '1';
        inner.style.zIndex   = '10';
      }
    }

    function onResize() {
      anchorLeft  = anchorWidth = null;
      absoluteSet = false;
      update();
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const scrollToStages = (e) => {
    e.preventDefault();
    stagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToAnimation = (e) => {
    e.preventDefault();
    animationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="baf-page">
      <Helmet>
        <title>TeachGenie Fellowship — A Movement Built by Educators</title>
        <meta name="description"
          content="Join the TeachGenie Fellowship. A merit-based recognition program for educators who inspire, refer, and grow the global TeachGenie community." />
      </Helmet>

      {/* ══════════════════════════════════════════════════════════════════════
          NAV
          ══════════════════════════════════════════════════════════════════════ */}
      <nav className="baf-nav">
        <div className="baf-nav-inner">
          <Link to="/" className="baf-nav-brand">
            <picture>
              <source srcSet="/TechGenieMascot.webp" type="image/webp" />
              <img src="/TechGenieMascot.png" alt="TeachGenie" className="baf-nav-logo" />
            </picture>
            <span className="baf-nav-wordmark">
              <span className="baf-nav-wordmark-name">TeachGenie<sup className="baf-nav-tm">™</sup></span>
              <span className="baf-nav-wordmark-tag">Fellowship</span>
            </span>
          </Link>

          <div className="baf-nav-links">
            <a href="#animation" className="baf-nav-link" onClick={scrollToAnimation}>Stages</a>
            <a href="#journey" className="baf-nav-link" onClick={scrollToStages}>How it works</a>
          </div>

          <a href="#join" className="baf-btn-solid baf-nav-cta" onClick={scrollToForm}>
            Become a Fellow <ArrowRight size={13} />
          </a>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════════════════
          §1  HERO
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="baf-hero" ref={heroRef}>

        <div className="baf-hero-inner">
          <div className="baf-hero-left">
            <span className="baf-label">The Fellowship</span>

            <h1 className="baf-h1">
              Teaching<br />
              shapes<br />
              the world.
            </h1>

            <p className="baf-hero-line">
              A merit-based recognition for educators who inspire,
              refer, and grow the global TeachGenie community.
            </p>

            <div className="baf-hero-cta">
              <a href="#join" className="baf-btn-solid" onClick={scrollToForm}>
                Become a Fellow <ArrowRight size={14} />
              </a>
              <a href="#journey" className="baf-btn-text" onClick={scrollToStages}>
                See the stages ↓
              </a>
            </div>
          </div>

          <motion.div className="baf-hero-wall" style={{ y: wallY }}>
            <FellowScrollWall />
          </motion.div>
        </div>

        <div className="baf-trio">
          <span>0 fees</span>
          <span className="baf-trio-dot" />
          <span>Merit only</span>
          <span className="baf-trio-dot" />
          <span>Lifetime access</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          §2  ANIMATION
          ══════════════════════════════════════════════════════════════════════ */}
      <div ref={animationRef}>
        <FellowshipAnimation />
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          §3  YOUR JOURNEY — sticky left + scrolling right (Eduaide-style)
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="baf-journey" id="journey" ref={stagesRef}>
        <div className="baf-container">
          <div className="baf-sj-wrap">

            {/* ── Sticky left panel — outer holds grid space, inner gets JS fixed ── */}
            <div className="baf-sj-left" ref={stickyOuter}>
              <div className="baf-sj-left-inner" ref={stickyInner}>
                <p className="baf-section-label">Fellowship stages</p>
                <h2 className="baf-h2">
                  Four milestones.<br />
                  <em>Every number.<br />Every rule.</em>
                </h2>
                <p className="baf-sj-sub">
                  Every referral is permanently recorded as YOM coins —
                  your community impact, quantified. Here is exactly
                  what it takes to rise.
                </p>
                <nav className="baf-sj-nav" aria-label="Fellowship levels">
                  {FELLOWSHIP_LEVELS.map((level) => (
                    <div key={level.key} className="baf-sj-nav-item">
                      <span className="baf-sj-nav-dot" style={{ background: level.color }} />
                      <span className="baf-sj-nav-name">{level.label}</span>
                    </div>
                  ))}
                </nav>
              </div>
            </div>

            {/* ── Right: scrolling feature sections ────────────────────────── */}
            <div className="baf-sj-right">
              {FELLOWSHIP_LEVELS.map((level, i) => (
                <ScrollFeature key={level.key} level={level} i={i} />
              ))}

              {/* YOM earning rates */}
              <motion.div className="baf-yom-row"
                variants={stagger} initial="hidden" whileInView="visible" viewport={VP}>
                {YOM_RATES.map((r) => (
                  <motion.div key={r.tier} className="baf-yom-cell" variants={up()}>
                    <span className="baf-yom-num">+{r.yoms}</span>
                    <span className="baf-yom-label">YOMs</span>
                    <span className="baf-yom-tier">{r.tier}</span>
                  </motion.div>
                ))}
              </motion.div>

              {/* Upgrade bonus note */}
              <motion.div className="baf-upgrade-note"
                variants={up(0.1)} initial="hidden" whileInView="visible" viewport={VP}>
                <span className="baf-upgrade-dot">&#9679;</span>
                <p>
                  <strong>Upgrade bonus:</strong> If someone you referred moves from Free to a paid plan,
                  you automatically earn the YOM difference — e.g., Free to Gold earns you +50 more YOMs.
                </p>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Join Form ─────────────────────────────────────────────── */}
      <div ref={formRef} id="join">
        <JoinForm />
      </div>

      <Footer />
    </div>
  );
}

// min/max are the digit counts expected AFTER the dial code
const COUNTRIES = [
  { name: 'India',                dial: '+91',  min: 10, max: 10 },
  { name: 'Afghanistan',          dial: '+93',  min: 9,  max: 9  },
  { name: 'Australia',            dial: '+61',  min: 9,  max: 9  },
  { name: 'Bangladesh',           dial: '+880', min: 10, max: 10 },
  { name: 'Brazil',               dial: '+55',  min: 10, max: 11 },
  { name: 'Canada',               dial: '+1',   min: 10, max: 10 },
  { name: 'China',                dial: '+86',  min: 11, max: 11 },
  { name: 'Egypt',                dial: '+20',  min: 10, max: 10 },
  { name: 'Ethiopia',             dial: '+251', min: 9,  max: 9  },
  { name: 'France',               dial: '+33',  min: 9,  max: 9  },
  { name: 'Germany',              dial: '+49',  min: 10, max: 11 },
  { name: 'Ghana',                dial: '+233', min: 9,  max: 9  },
  { name: 'Indonesia',            dial: '+62',  min: 9,  max: 12 },
  { name: 'Iran',                 dial: '+98',  min: 10, max: 10 },
  { name: 'Iraq',                 dial: '+964', min: 10, max: 10 },
  { name: 'Israel',               dial: '+972', min: 9,  max: 9  },
  { name: 'Italy',                dial: '+39',  min: 9,  max: 10 },
  { name: 'Japan',                dial: '+81',  min: 10, max: 10 },
  { name: 'Jordan',               dial: '+962', min: 9,  max: 9  },
  { name: 'Kenya',                dial: '+254', min: 9,  max: 9  },
  { name: 'Malaysia',             dial: '+60',  min: 9,  max: 10 },
  { name: 'Mexico',               dial: '+52',  min: 10, max: 10 },
  { name: 'Morocco',              dial: '+212', min: 9,  max: 9  },
  { name: 'Myanmar',              dial: '+95',  min: 8,  max: 10 },
  { name: 'Nepal',                dial: '+977', min: 10, max: 10 },
  { name: 'Netherlands',          dial: '+31',  min: 9,  max: 9  },
  { name: 'New Zealand',          dial: '+64',  min: 8,  max: 9  },
  { name: 'Nigeria',              dial: '+234', min: 10, max: 10 },
  { name: 'Pakistan',             dial: '+92',  min: 10, max: 10 },
  { name: 'Philippines',          dial: '+63',  min: 10, max: 10 },
  { name: 'Poland',               dial: '+48',  min: 9,  max: 9  },
  { name: 'Portugal',             dial: '+351', min: 9,  max: 9  },
  { name: 'Russia',               dial: '+7',   min: 10, max: 10 },
  { name: 'Saudi Arabia',         dial: '+966', min: 9,  max: 9  },
  { name: 'Singapore',            dial: '+65',  min: 8,  max: 8  },
  { name: 'South Africa',         dial: '+27',  min: 9,  max: 9  },
  { name: 'South Korea',          dial: '+82',  min: 9,  max: 10 },
  { name: 'Spain',                dial: '+34',  min: 9,  max: 9  },
  { name: 'Sri Lanka',            dial: '+94',  min: 9,  max: 9  },
  { name: 'Sudan',                dial: '+249', min: 9,  max: 9  },
  { name: 'Sweden',               dial: '+46',  min: 9,  max: 9  },
  { name: 'Switzerland',          dial: '+41',  min: 9,  max: 9  },
  { name: 'Tanzania',             dial: '+255', min: 9,  max: 9  },
  { name: 'Thailand',             dial: '+66',  min: 9,  max: 9  },
  { name: 'Turkey',               dial: '+90',  min: 10, max: 10 },
  { name: 'Uganda',               dial: '+256', min: 9,  max: 9  },
  { name: 'Ukraine',              dial: '+380', min: 9,  max: 9  },
  { name: 'United Arab Emirates', dial: '+971', min: 9,  max: 9  },
  { name: 'United Kingdom',       dial: '+44',  min: 10, max: 10 },
  { name: 'United States',        dial: '+1',   min: 10, max: 10 },
  { name: 'Vietnam',              dial: '+84',  min: 9,  max: 10 },
];

function validate(form) {
  const errs = {};
  if (!form.name.trim()) errs.name = 'Full name is required';
  else if (form.name.trim().length < 2) errs.name = 'Enter at least 2 characters';

  if (!form.designation.trim()) errs.designation = 'Designation is required';

  if (!form.university.trim()) errs.university = 'Institution name is required';

  if (!form.country) errs.country = 'Please select your country';

  const digits = form.phone.replace(/\D/g, '');
  const countryRule = COUNTRIES.find(c => c.name === form.country);
  if (!form.phone.trim()) {
    errs.phone = 'Phone number is required';
  } else if (countryRule) {
    if (countryRule.min === countryRule.max) {
      if (digits.length !== countryRule.min)
        errs.phone = `Enter exactly ${countryRule.min} digits for ${form.country}`;
    } else {
      if (digits.length < countryRule.min || digits.length > countryRule.max)
        errs.phone = `Enter ${countryRule.min}–${countryRule.max} digits for ${form.country}`;
    }
  } else if (digits.length < 6 || digits.length > 15) {
    errs.phone = 'Enter a valid phone number';
  }

  if (!form.email.trim()) errs.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address';

  return errs;
}

function CountryDropdown({ value, onChange, onBlur, hasError }) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const wrapRef               = useRef(null);
  const searchRef             = useRef(null);

  const filtered = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    setTimeout(() => searchRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onBlur]);

  const selected = COUNTRIES.find(c => c.name === value);

  const trigger = {
    width: '100%', padding: '11px 14px', borderRadius: 10,
    border: `1.5px solid ${hasError ? '#ef4444' : open ? '#0f172a' : '#e8edf2'}`,
    background: '#f8fafc', fontSize: 14, fontWeight: 500,
    color: selected ? '#0f172a' : '#94a3b8',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', outline: 'none', fontFamily: 'inherit',
    boxSizing: 'border-box', transition: 'border-color 0.15s',
    userSelect: 'none',
  };

  const panel = {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
    background: '#ffffff', borderRadius: 12,
    border: '1.5px solid #e8edf2',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
    zIndex: 999, overflow: 'hidden',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" style={trigger} onClick={() => setOpen(o => !o)}>
        <span>{selected ? selected.name : 'Select country'}</span>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>
          <path d="M1 1l5 5 5-5" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>

      {open && (
        <div style={panel}>
          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', borderRadius: 8, padding: '7px 12px', border: '1px solid #e8edf2' }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="6" cy="6" r="4.5" stroke="#94a3b8" strokeWidth="1.4"/>
                <path d="M10 10l2.5 2.5" stroke="#94a3b8" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              <input
                ref={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search country…"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#0f172a', width: '100%', fontFamily: 'inherit' }}
              />
            </div>
          </div>

          {/* List */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '16px 16px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>No results</div>
            ) : filtered.map(c => (
              <div
                key={c.name}
                onMouseDown={() => { onChange(c.name); setOpen(false); onBlur?.(); }}
                style={{
                  padding: '10px 16px', fontSize: 14, cursor: 'pointer',
                  color: c.name === value ? '#1e3a8a' : '#0f172a',
                  background: c.name === value ? '#eff6ff' : 'transparent',
                  fontWeight: c.name === value ? 600 : 400,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (c.name !== value) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (c.name !== value) e.currentTarget.style.background = 'transparent'; }}
              >
                <span>{c.name}</span>
                <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>{c.dial}</span>
                {c.name === value && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 6, flexShrink: 0 }}>
                    <path d="M2 7l4 4 6-7" stroke="#1e3a8a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function JoinForm() {
  const [form, setForm] = useState({
    name: '', designation: '', university: '',
    country: '', phone: '', email: '', description: '',
  });
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  // 'idle' | 'checking' | 'found' | 'not_found'
  const [emailStatus, setEmailStatus] = useState('idle');

  const dialCode = COUNTRIES.find(c => c.name === form.country)?.dial || '';
  const errors = validate(form);
  const showErr = (f) => (touched[f] || submitAttempted) && errors[f];

  const update = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (k === 'email') setEmailStatus('idle');
  };
  const touch = (k) => setTouched(p => ({ ...p, [k]: true }));

  const checkEmail = async () => {
    touch('email');
    const emailVal = form.email.trim();
    // Only check if format is valid
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) return;
    setEmailStatus('checking');
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${API_BASE}/api/v1/public/check-email?email=${encodeURIComponent(emailVal)}`);
      const data = await res.json();
      setEmailStatus(data.registered ? 'found' : 'not_found');
    } catch {
      setEmailStatus('idle');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) return;
    if (emailStatus === 'not_found') return;
    if (emailStatus !== 'found') {
      // Haven't checked yet — check now before submitting
      await checkEmail();
      return;
    }
    setServerError('');
    try {
      setSubmitting(true);
      await submitJoinRequest({
        ...form,
        location: form.country,
        phone: dialCode + ' ' + form.phone,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err.message || '';
      if (msg === 'no_account') {
        setEmailStatus('not_found');
      } else {
        setServerError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const base = {
    width: '100%', padding: '12px 15px', borderRadius: 10,
    fontSize: 14, fontWeight: 500, outline: 'none',
    fontFamily: 'inherit', color: '#0A1628', boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };
  const inp  = (f) => ({ ...base, border: `1.5px solid ${showErr(f) ? '#ef4444' : '#dde3ed'}`, background: '#FAFBFD' });
  const lbl  = { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#64748b', marginBottom: 6, display: 'block' };
  const err  = (f) => showErr(f) ? <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4, fontWeight: 500 }}>{errors[f]}</p> : null;

  const sectionBg = {
    padding: '100px 40px',
    background: 'linear-gradient(160deg, #edf1ff 0%, #fafafa 45%, #fffbf2 100%)',
  };

  const premiumCard = {
    maxWidth: 700,
    margin: '0 auto',
    background: '#ffffff',
    borderRadius: 24,
    border: '1px solid rgba(200,128,10,0.14)',
    boxShadow:
      '0 2px 6px rgba(0,34,77,0.04), 0 12px 40px rgba(0,34,77,0.09), 0 40px 100px rgba(0,34,77,0.05)',
    overflow: 'hidden',
  };

  if (submitted) {
    return (
      <section style={sectionBg} className="baf-join-section">
        <div style={{ ...premiumCard, padding: '64px 48px', textAlign: 'center' }} className="baf-join-card baf-join-card--success">
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#d1fae5,#a7f3d0)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <CheckCircle size={32} style={{ color: '#059669' }} />
          </div>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0A1628', marginBottom: 10, letterSpacing: '-0.4px' }}>Application Submitted!</h2>
          <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.6 }}>We'll review your application and get back to you within 24 hours.</p>
        </div>
      </section>
    );
  }

  return (
    <section style={sectionBg} className="baf-join-section">
      <div style={premiumCard} className="baf-join-card">

        {/* Gold accent bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #C8800A 0%, #f59e0b 50%, #C8800A 100%)' }} />

        {/* Card header */}
        <div className="baf-join-card-header" style={{ textAlign: 'center', padding: '44px 48px 32px', borderBottom: '1px solid #f3ece0' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#fff8e7,#fdefc8)', border: '1px solid rgba(200,128,10,0.28)', borderRadius: 99, padding: '5px 16px', marginBottom: 18 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#C8800A', flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#A36508' }}>YOM Fellowship Program</span>
          </div>
          <h2 style={{ fontSize: 30, fontWeight: 900, color: '#0A1628', marginBottom: 10, letterSpacing: '-0.6px', lineHeight: 1.15, fontFamily: "'Outfit', sans-serif" }}>Become a Fellow</h2>
          <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.6, maxWidth: 400, margin: '0 auto' }}>Fill in your details and we'll review your application</p>
        </div>

        {/* Form body */}
        <div className="baf-join-body" style={{ padding: '36px 48px 52px' }}>
          <form onSubmit={handleSubmit} noValidate className="baf-join-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 24px' }}>

            {/* Name */}
            <div>
              <label style={lbl}>Full Name *</label>
              <input className="baf-join-input" style={inp('name')} value={form.name}
                onChange={e => update('name', e.target.value)}
                onBlur={() => touch('name')}
                placeholder="" />
              {err('name')}
            </div>

            {/* Designation */}
            <div>
              <label style={lbl}>Designation *</label>
              <input className="baf-join-input" style={inp('designation')} value={form.designation}
                onChange={e => update('designation', e.target.value)}
                onBlur={() => touch('designation')}
                placeholder="" />
              {err('designation')}
            </div>

            {/* University */}
            <div>
              <label style={lbl}>University / School *</label>
              <input className="baf-join-input" style={inp('university')} value={form.university}
                onChange={e => update('university', e.target.value)}
                onBlur={() => touch('university')}
                placeholder="" />
              {err('university')}
            </div>

            {/* Country dropdown */}
            <div>
              <label style={lbl}>Country *</label>
              <CountryDropdown
                value={form.country}
                onChange={v => update('country', v)}
                onBlur={() => touch('country')}
                hasError={showErr('country')}
              />
              {err('country')}
            </div>

            {/* Phone with dial code */}
            <div>
              <label style={lbl}>Phone *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ ...base, border: `1.5px solid ${showErr('phone') ? '#ef4444' : '#dde3ed'}`, background: 'linear-gradient(135deg,#fff9ee,#fef3da)', width: 72, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A36508', fontWeight: 700, fontSize: 12 }}>
                  {dialCode || '—'}
                </div>
                <input className="baf-join-input" style={{ ...inp('phone'), flex: 1 }}
                  value={form.phone}
                  onChange={e => update('phone', e.target.value.replace(/[^\d\s\-]/g, ''))}
                  onBlur={() => touch('phone')}
                  placeholder="" />
              </div>
              {err('phone')}
            </div>

            {/* Email */}
            <div>
              <label style={lbl}>Email *</label>
              <div style={{ position: 'relative' }}>
                <input className="baf-join-input" style={{
                  ...inp('email'),
                  border: `1.5px solid ${
                    emailStatus === 'not_found' ? '#ef4444'
                    : emailStatus === 'found'   ? '#10b981'
                    : showErr('email')          ? '#ef4444'
                    : '#dde3ed'
                  }`,
                }} type="email" value={form.email}
                  onChange={e => update('email', e.target.value)}
                  onBlur={checkEmail}
                  placeholder="" />
                {emailStatus === 'checking' && (
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8' }}>Checking…</span>
                )}
                {emailStatus === 'found' && (
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontSize: 16 }}>✓</span>
                )}
              </div>
              {err('email')}
              {emailStatus === 'not_found' && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4, fontWeight: 500 }}>
                  No TeachGenie account found.{' '}
                  <a href="/signup" style={{ color: '#1e3a8a', fontWeight: 700, textDecoration: 'underline' }}>Create an account →</a>
                </p>
              )}
            </div>

            {/* Description */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>About You <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#b0bec9' }}>(optional)</span></label>
              <textarea className="baf-join-textarea" style={{ ...base, border: '1.5px solid #dde3ed', background: '#FAFBFD', minHeight: 88, resize: 'vertical', lineHeight: 1.55 }}
                value={form.description}
                onChange={e => update('description', e.target.value)}
                placeholder="" />
            </div>

            {serverError && (
              <p style={{ gridColumn: '1 / -1', color: '#ef4444', fontSize: 13, fontWeight: 600 }}>{serverError}</p>
            )}

            {/* Submit */}
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', marginTop: 8 }}>
              <button
                type="submit"
                disabled={submitting || emailStatus === 'not_found'}
                className="baf-submit-btn"
                style={{
                  padding: '15px 56px', borderRadius: 14,
                  background: (submitting || emailStatus === 'not_found')
                    ? '#e2e8f0'
                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: (submitting || emailStatus === 'not_found') ? '#94a3b8' : '#ffffff',
                  fontSize: 15, fontWeight: 700, border: 'none',
                  cursor: (submitting || emailStatus === 'not_found') ? 'not-allowed' : 'pointer',
                  boxShadow: (submitting || emailStatus === 'not_found')
                    ? 'none'
                    : '0 4px 18px rgba(245,158,11,0.38), 0 2px 6px rgba(0,0,0,0.06)',
                  letterSpacing: '0.03em', transition: 'all 0.2s',
                }}>
                {submitting ? 'Submitting…' : 'Submit Application'}
              </button>

            </div>

          </form>
        </div>
      </div>
    </section>
  );
}
